import { ColorMode } from './domain.js';
export class PageRangeError extends Error {
}
/**
 * Parses a page range like "1-3,5,8-10" into a sorted list of unique 1-based page numbers.
 * An empty/undefined range selects every page.
 */
export function parsePageRange(range, totalPages) {
    if (totalPages < 1)
        throw new PageRangeError('Document has no pages');
    const trimmed = range?.trim();
    if (!trimmed)
        return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = new Set();
    for (const rawPart of trimmed.split(',')) {
        const part = rawPart.trim();
        if (!part)
            continue;
        const match = /^(\d+)\s*(?:-\s*(\d+))?$/.exec(part);
        if (!match)
            throw new PageRangeError(`Invalid page range "${part}"`);
        const start = Number(match[1]);
        const end = match[2] === undefined ? start : Number(match[2]);
        if (start < 1 || end < start)
            throw new PageRangeError(`Invalid page range "${part}"`);
        if (end > totalPages) {
            throw new PageRangeError(`Page ${end} is out of range (document has ${totalPages} pages)`);
        }
        for (let p = start; p <= end; p++)
            pages.add(p);
    }
    if (pages.size === 0)
        throw new PageRangeError('Page range selects no pages');
    return [...pages].sort((a, b) => a - b);
}
export function quoteFile(pageCount, options, pricing) {
    const selectedPages = parsePageRange(options.pageRange, pageCount).length;
    const isColor = options.colorMode === ColorMode.COLOR;
    if (isColor && !pricing.colorEnabled)
        throw new PageRangeError('Colour printing is not available');
    if (options.duplex && !pricing.duplexEnabled) {
        throw new PageRangeError('Double-sided printing is not available');
    }
    const single = isColor ? pricing.colorPricePerPage : pricing.bwPricePerPage;
    const duplexPrice = isColor ? pricing.colorDuplexPricePerPage : pricing.bwDuplexPricePerPage;
    const pricePerSide = options.duplex ? (duplexPrice ?? single) : single;
    const printedSides = selectedPages * options.copies;
    const sheetsPerCopy = options.duplex ? Math.ceil(selectedPages / 2) : selectedPages;
    const sheets = sheetsPerCopy * options.copies;
    return { selectedPages, printedSides, sheets, pricePerSide, amount: printedSides * pricePerSide };
}
export function quoteOrder(files, pricing) {
    const quotes = files.map((f) => quoteFile(f.pageCount, f.options, pricing));
    return {
        files: quotes,
        totalSheets: quotes.reduce((s, q) => s + q.sheets, 0),
        totalAmount: quotes.reduce((s, q) => s + q.amount, 0),
    };
}
/** Formats paise as an INR string, e.g. 1250 -> "₹12.50". */
export function formatINR(paise) {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(paise / 100);
}
//# sourceMappingURL=pricing.js.map